'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import { useLocalizedPath } from '@/src/hooks/useLocalizedPath';
import { useTurnstile } from '@/src/hooks/useTurnstile';
import { resolveErrorMessage } from '@/src/utils/errorInterpolate';
import { gatewayClient } from '@/src/api';
import styles from './Auth.module.css';

interface ForgotPasswordFormProps {
  translations: {
    title: string;
    subtitle: string;
    emailLabel: string;
    emailPlaceholder: string;
    submit: string;
    submitting: string;
    successMessage: string;
    backToLogin: string;
    errors: Record<string, string>;
  };
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ translations }) => {
  const locale = useLocale();
  const { localizeFullPath } = useLocalizedPath();
  const turnstile = useTurnstile({ action: 'forgot-password' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (turnstile.isRequired && !turnstile.token) {
      setError(translations.errors.DEFAULT);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      await gatewayClient.auth.forgotPassword(email, locale, turnstile.token ?? undefined);
      setSuccess(true);
    } catch (err) {
      turnstile.reset();
      setError(resolveErrorMessage(err, translations.errors));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        <div className={styles.authLogo}>
          <Link href="/">
            <img src="/short-logo-black.svg" alt="ACEB" />
          </Link>
        </div>
        <h1 className={styles.authTitle}>{translations.title}</h1>
        <p className={styles.authSubtitle}>{translations.subtitle}</p>

        {error && (
          <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
        )}

        {success ? (
          <>
            <div className={`${styles.alert} ${styles.alertSuccess}`}>{translations.successMessage}</div>
            <p className={styles.authFooter}>
              <Link href={localizeFullPath('/login')}>{translations.backToLogin}</Link>
            </p>
          </>
        ) : (
          <form className={styles.authForm} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="forgot-email">
                {translations.emailLabel}
              </label>
              <input
                id="forgot-email"
                className={styles.formInput}
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                placeholder={translations.emailPlaceholder}
                autoComplete="email"
                required
              />
            </div>

            {turnstile.widget}

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting || (turnstile.isRequired && !turnstile.token)}
            >
              {isSubmitting ? (
                <>
                  <span className={styles.spinner} />
                  {translations.submitting}
                </>
              ) : (
                translations.submit
              )}
            </button>

            <p className={styles.authFooter}>
              <Link href={localizeFullPath('/login')}>{translations.backToLogin}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
